import { Box, Typography } from "@mui/material";
import React, { useMemo } from "react";

import { monospaceStyle } from "../../theme";
import { ProductDetail } from "../../types/productPlane";
import SourceTimeline from "../Detail/SourceTimeline";
import { dateRange } from "../Detail/format";
import OpenInValueChainLink from "../KindDetail/OpenInValueChainLink";

interface ProductEvidenceProps {
  product: ProductDetail;
  // Drawn for a printed page: without the controls that act on the screen
  print?: boolean;
}

const Dated: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box component="span" sx={monospaceStyle}>
    {children}
  </Box>
);

/**
 * How much evidence there is for a product and from when: the number of
 * its sources, when they appeared over time, the first and the latest, and
 * the step into the value chain.
 */
const ProductEvidence: React.FC<ProductEvidenceProps> = ({ product, print = false }) => {
  const sourceDates = useMemo(
    () => product.sources.items.map((source) => source.date),
    [product.sources.items],
  );
  const range = useMemo(() => dateRange(sourceDates), [sourceDates]);

  return (
    <Box
      role="group"
      aria-label="Evidence"
      sx={{
        display: "grid",
        gridTemplateColumns: "auto minmax(0, 1fr)",
        alignItems: "center",
        columnGap: 2.25,
        rowGap: 0.75,
        px: 1.75,
        py: 1.5,
        backgroundColor: "background.raised",
        border: 1,
        borderColor: "divider",
        borderRadius: (theme) => `${theme.radius.surface}px`,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "baseline", gap: 1 }}>
        <Box
          component="span"
          sx={{
            ...monospaceStyle,
            fontSize: "1.125rem",
            fontWeight: 500,
            lineHeight: 1.2,
          }}
        >
          {product.source_count}
        </Box>{" "}
        <Typography variant="caption" color="text.secondary">
          {product.source_count === 1 ? "source" : "sources"}
        </Typography>
      </Box>
      <SourceTimeline dates={sourceDates} />
      {(range || product.categories.length > 0) && (
        // Across the width: when the sources appeared, and the step into
        // the value chain
        <Box
          sx={{
            gridColumn: "1 / -1",
            display: "flex",
            alignItems: "baseline",
            flexWrap: "wrap",
            columnGap: 2,
            rowGap: 0.5,
          }}
        >
          {range && (
            <Typography variant="caption" color="text.disabled">
              first <Dated>{range.first}</Dated> · latest{" "}
              <Dated>{range.latest}</Dated>
            </Typography>
          )}
          {/* The response names no rank, so its class roots the chain */}
          {!print && product.categories.length > 0 && (
            <Box sx={{ ml: "auto" }}>
              <OpenInValueChainLink
                root={{
                  type: "class",
                  id: product.categories[0].category.id,
                }}
              />
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

export default ProductEvidence;
